import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  LogOut,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
} from "lucide-react";

function AdminPlayers() {
  const navigate = useNavigate();

  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const api = import.meta.env.VITE_API_URL;

  const token = localStorage.getItem("admin_token");
  const adminId = localStorage.getItem("admin_id");

  // --------------------------------------------------
  // CHECK ADMIN LOGIN
  // --------------------------------------------------

  useEffect(() => {
    if (!token || adminId !== "1") {
      navigate("/admin", { replace: true });
      return;
    }

    fetchPlayers();
  }, []);

  // --------------------------------------------------
  // GET ALL PLAYERS
  // --------------------------------------------------

  const fetchPlayers = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${api}/api/players`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to load players.");
      }

      const playerList = Array.isArray(data)
        ? data
        : data.players || data.data || [];

      // Newest player first
      playerList.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setPlayers(playerList);
      setFilteredPlayers(playerList);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load players.");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // SEARCH
  // --------------------------------------------------

  useEffect(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      setFilteredPlayers(players);
      return;
    }

    const result = players.filter((player) => {
      return (
        String(player.id || "")
          .toLowerCase()
          .includes(keyword) ||
        String(player.name || "")
          .toLowerCase()
          .includes(keyword) ||
        String(player.email || "")
          .toLowerCase()
          .includes(keyword) ||
        String(player.phone || "")
          .toLowerCase()
          .includes(keyword)
      );
    });

    setFilteredPlayers(result);
  }, [search, players]);

  // --------------------------------------------------
  // CHANGE PLAYER STATUS
  // --------------------------------------------------

  const changeStatus = async (playerId, status) => {
    const message =
      status === 1 ? "Approve this player?" : "Reject this player?";

    if (!window.confirm(message)) {
      return;
    }

    setUpdatingId(playerId);
    setError("");

    try {
      const response = await fetch(
        `${api}/api/admin/players/${playerId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status,
          }),
        },
      );

      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to update status.");
      }

      // Update UI immediately
      setPlayers((currentPlayers) =>
        currentPlayers.map((player) =>
          player.id === playerId
            ? {
                ...player,
                status,
              }
            : player,
        ),
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to update player status.");
    } finally {
      setUpdatingId(null);
    }
  };

  // --------------------------------------------------
  // LOGOUT
  // --------------------------------------------------

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_id");

    navigate("/admin/login", { replace: true });
  };

  // --------------------------------------------------
  // STATUS
  // --------------------------------------------------

  const getStatus = (status) => {
    if (Number(status) === 1) {
      return {
        text: "Approved",
        className: "border-green-500/20 bg-green-500/10 text-green-400",
        icon: CheckCircle,
      };
    }

    return {
      text: "Pending",
      className: "border-yellow-500/20 bg-yellow-500/10 text-yellow-400",
      icon: Clock,
    };
  };

  // --------------------------------------------------
  // COUNTS
  // --------------------------------------------------

  const totalPlayers = players.length;

  const approvedPlayers = players.filter(
    (player) => Number(player.status) === 1,
  ).length;

  const pendingPlayers = players.filter(
    (player) => Number(player.status) === 0,
  ).length;

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#100404] via-[#250707] to-black text-white">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-red-600 to-orange-500 shadow-lg shadow-red-900/30">
              <Trophy className="h-6 w-6 text-white" />
            </div>

            <div>
              <h1 className="font-black">BPL Admin</h1>

              <p className="text-xs text-gray-500">
                Badgaw Premier League • Season 2
              </p>
            </div>
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-sm font-semibold text-red-400 transition hover:bg-red-500/20"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* CONTENT */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-10">
        {/* TITLE */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-orange-400">
            <ShieldCheck className="h-5 w-5" />

            <span className="text-sm font-bold uppercase tracking-wider">
              Administrator
            </span>
          </div>

          <h2 className="mt-2 text-3xl font-black sm:text-4xl">
            Player Management
          </h2>

          <p className="mt-2 text-sm text-gray-400">
            Review registrations and approve or reject players.
          </p>
        </div>

        {/* STATS */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Total */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Players</p>

                <p className="mt-1 text-3xl font-black">{totalPlayers}</p>
              </div>

              <div className="rounded-xl bg-blue-500/10 p-3">
                <User className="h-6 w-6 text-blue-400" />
              </div>
            </div>
          </div>

          {/* Pending */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>

                <p className="mt-1 text-3xl font-black text-yellow-400">
                  {pendingPlayers}
                </p>
              </div>

              <div className="rounded-xl bg-yellow-500/10 p-3">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Approved */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>

                <p className="mt-1 text-3xl font-black text-green-400">
                  {approvedPlayers}
                </p>
              </div>

              <div className="rounded-xl bg-green-500/10 p-3">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* SEARCH + REFRESH */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />

            <input
              type="text"
              placeholder="Search by player ID, name, email or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.05] pl-12 pr-4 text-sm text-white outline-none placeholder:text-gray-600 focus:border-orange-500"
            />
          </div>

          <button
            onClick={fetchPlayers}
            disabled={loading}
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-5 text-sm font-semibold text-gray-300 transition hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* LOADING */}
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-orange-500" />

              <p className="mt-4 text-sm text-gray-500">Loading players...</p>
            </div>
          </div>
        ) : filteredPlayers.length === 0 ? (
          /* EMPTY */
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-12 text-center">
            <User className="mx-auto h-12 w-12 text-gray-700" />

            <p className="mt-4 font-semibold text-gray-400">No players found</p>
          </div>
        ) : (
          /* PLAYERS */
          <div className="space-y-4">
            {filteredPlayers.map((player) => {
              const status = getStatus(player.status);
              const StatusIcon = status.icon;

              return (
                <div
                  key={player.id}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.05] shadow-xl backdrop-blur-xl"
                >
                  <div className="p-5 sm:p-6">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                      {/* PLAYER INFO */}
                      <div className="flex min-w-0 items-start gap-4">
                        {/* PHOTO */}
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
                          {player.photo ? (
                            <img
                              src={player.photo}
                              alt={player.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <User className="h-7 w-7 text-gray-600" />
                            </div>
                          )}
                        </div>

                        {/* DETAILS */}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate text-lg font-black">
                              {player.name}
                            </h3>

                            <span className="rounded-md bg-orange-500/10 px-2 py-1 text-xs font-bold text-orange-400">
                              #{player.id}
                            </span>
                          </div>

                          <div className="mt-2 grid gap-1 text-xs text-gray-500 sm:grid-cols-2 sm:gap-x-6">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5" />
                              <span className="truncate">{player.email}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5" />
                              <span>{player.phone}</span>
                            </div>

                            <div className="flex items-center gap-2 sm:col-span-2">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{player.address}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* STATUS + ACTION */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        {/* STATUS */}
                        <div
                          className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold ${status.className}`}
                        >
                          <StatusIcon className="h-4 w-4" />
                          {status.text}
                        </div>

                        {/* APPROVE */}
                        {Number(player.status) !== 1 && (
                          <button
                            onClick={() => changeStatus(player.id, 1)}
                            disabled={updatingId === player.id}
                            className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {updatingId === player.id ? (
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                            Approve
                          </button>
                        )}

                        {/* REJECT */}
                        {Number(player.status) === 1 && (
                          <button
                            onClick={() => changeStatus(player.id, 0)}
                            disabled={updatingId === player.id}
                            className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {updatingId === player.id ? (
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                            Reject
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}

export default AdminPlayers;
